import React, { useState, useEffect, useRef } from 'react';
import { FormulaEditor } from 'common/components/FormulaEditor/FormulaEditor';
import { SetupHighlightingListeners } from 'common/components/FormulaEditor/customHooks/useAutocompleteHighlighting';

interface AdvancedFormulaEditorProps {
  autocompleteValues: string[];
  formula: string;
  onFormulaChange: (formula: string) => void;
  setupHighlightingListeners: SetupHighlightingListeners;
}
/**
 * AdvancedFormulaEditor component is not a finalized implementation.
 * This serves as a wrapper of the common FormulaEditor.
 * - stubs out an async formula validation function.
 * - Holds the formula in state. Nothing is done with it yet.
 *
 * This is all related to the work gated by the feature flag enable_table_advanced_totaling
 *
 * @warning This component is a work in progress and will need change significantly before release.
 *
 * @warning The forge dialog that is wrapping these editors must have the id of 'advanced-totaling-dialog'
 */
export const AdvancedFormulaEditor: React.FC<AdvancedFormulaEditorProps> = ({
  autocompleteValues,
  formula,
  onFormulaChange,
  setupHighlightingListeners,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isDialogLoaded, setIsDialogLoaded] = useState(false);

  // Formula change handler that updates both internal state and external callback
  const handleFormulaChange = (val: string) => {
    onFormulaChange(val);
  };

  // Simulated async formula validation function
  const validateFormula = async (val: string): Promise<boolean> => {
    // This is just a simulation for validation, real validation will be needed
    await new Promise((resolve) => setTimeout(resolve, 2000));
    return val === 'fail' ? false : true;
  };

  // We need to wait for the dialog element to be present in the DOM before rendering the FormulaEditor
  // This is because the formula editor will move the Ace editor's autocomplete popup into the forge dialog to prevent
  // it rendering under the forge dialog.
  useEffect(() => {
    const checkForElement = () => {
      const element = document.getElementById('advanced-totaling-dialog');
      if (element) {
        setIsDialogLoaded(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkForElement()) {
      return;
    }

    // Watch for DOM changes
    const observer = new MutationObserver(() => {
      if (checkForElement()) {
        observer.disconnect();
      }
    });

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, []);

  return (
    <div ref={containerRef} data-testid="advanced-formula-editor">
      {isDialogLoaded && (
        <FormulaEditor
          autocompleteValues={autocompleteValues}
          currentFormula={formula}
          updateFormula={handleFormulaChange}
          validateFormula={validateFormula}
          forgeDialogId="advanced-totaling-dialog"
          setupHighlightingListeners={setupHighlightingListeners}
        />
      )}
    </div>
  );
};
